import React, { useState } from 'react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ChevronDown, Check, X, Search } from 'lucide-react'

export interface Option {
  id: string | number
  label: string
  sublabel?: string
}

interface Props {
  title: string
  options: Option[]
  selectedValues: (string | number)[]
  onChange?: (selected: (string | number)[]) => void
  onSelectionChange?: (selected: (string | number)[]) => void
  placeholder?: string
  icon?: React.ReactNode
  className?: string
}

export function MultiSelectPopover({
  title,
  options,
  selectedValues,
  onChange,
  onSelectionChange,
  placeholder = 'Search...',
  icon,
  className = '',
}: Props) {
  const handleChange = onSelectionChange || onChange || (() => {})
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const filteredOptions = options.filter(
    (opt) =>
      opt.label.toLowerCase().includes(search.toLowerCase()) ||
      (opt.sublabel && opt.sublabel.toLowerCase().includes(search.toLowerCase()))
  )

  const stringSelectedValues = selectedValues.map(String)

  const toggleOption = (id: string | number) => {
    const strId = String(id)
    if (stringSelectedValues.includes(strId)) {
      handleChange(selectedValues.filter((v) => String(v) !== strId))
    } else {
      handleChange([...selectedValues, id])
    }
  }

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation()
    handleChange([])
  }

  const selectedCount = selectedValues.length

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={`h-9 px-3 gap-1.5 justify-between font-normal hover:bg-accent ${className}`}
        >
          <div className="flex items-center gap-1.5 truncate max-w-[200px]">
            {icon}
            <span className="truncate">{title}</span>
            {selectedCount > 0 && (
              <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-semibold bg-primary/15 text-primary ml-0.5">
                {selectedCount}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-1 text-muted-foreground">
            {selectedCount > 0 && (
              <X
                className="h-3.5 w-3.5 hover:text-foreground cursor-pointer"
                onClick={clearSelection}
              />
            )}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-64 p-2 space-y-2 max-h-72 flex flex-col rounded-xl border-border bg-popover text-popover-foreground shadow-glass-md z-50"
      >
        {options.length > 5 && (
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs rounded-lg"
              autoFocus
            />
          </div>
        )}

        <div className="overflow-y-auto flex-1 space-y-0.5 pr-1 max-h-48">
          {filteredOptions.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-3">No matching options</div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = stringSelectedValues.includes(String(opt.id))
              return (
                <div
                  key={opt.id}
                  onClick={() => toggleOption(opt.id)}
                  className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-primary/10 text-primary font-medium'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  }`}
                >
                  <div className="truncate flex flex-col">
                    <span className="truncate">{opt.label}</span>
                    {opt.sublabel && (
                      <span className="text-[10px] text-muted-foreground truncate">{opt.sublabel}</span>
                    )}
                  </div>
                  {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0 ml-2" />}
                </div>
              )
            })
          )}
        </div>

        {selectedCount > 0 && (
          <div className="pt-1.5 border-t border-border flex justify-between items-center text-[11px] text-muted-foreground px-1">
            <span>{selectedCount} selected</span>
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={() => handleChange([])}
              className="h-auto p-0 text-primary font-medium text-xs hover:underline"
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
